// /lib/errors/handleAppError.ts

import type { AxiosError } from 'axios'

import { toast } from 'sonner'
import { ApiError, ValidationError } from './apiError'
import { BookingAction } from '../../state/booking.reducer'
import { BookingT } from '@/src/types/i18n'

/**
 * App-level error types your UI understands.
 * These are BUSINESS errors — not HTTP details.
 */
export type AppError =
  | 'cart_expired'
  | 'cart_not_found'
  | 'insufficient_availability'
  | 'payment_failed'
  | 'bad_request'
  | 'unauthorized'
  | 'network_error'
  | 'internal_error'
  | 'validation_error'
  | 'unknown'

/**
 * Optional: allow injecting custom behaviour like redirects
 */
type HandleOptions = {
  onValidationError?: (errors: ValidationError[]) => void
  onCartExpired?: () => void
  onCartNotFound?: () => void
  onUnauthorized?: () => void
  onInternalError?: () => void
}

/**
 * Map raw error -> AppError
 */
function mapApiError(error: unknown): AppError {
  // Network error (no response)
  if (isAxiosNetworkError(error)) {
    return 'network_error'
  }

  if (error instanceof ApiError) {
    const status = error.status
    const code = error.data?.errorCode

    // HTTP-based mapping
    if (status === 400) return 'bad_request'
    if (status === 401) return 'unauthorized'
    if (status === 404) return 'cart_not_found'

    if (status === 409) {
      if (code === 'CART_EXPIRED') return 'cart_expired'
      if (code === 'INSUFFICIENT_AVAILABILITY') return 'insufficient_availability'
    }

    if (status === 422) return 'validation_error'

    if (status && status >= 500) return 'internal_error'
    if (code === 'PAYMENT_FAILED') return 'payment_failed'
  }

  return 'unknown'
}

function isAxiosNetworkError(error: unknown): boolean {
  const err = error as AxiosError
  return !!err?.isAxiosError && !err.response
}

/**
 * Centralised UI error handler
 */
export function handleAppError(
  error: unknown,
  //options?: HandleOptions,
  args: { dispatch: React.Dispatch<BookingAction>; t: BookingT },
): AppError {
  const { dispatch, t } = args
  const appError = mapApiError(error)
  const options: HandleOptions = {}

  const setError = (error: string) => {
    dispatch({
      type: 'SET_ERROR',
      error: {
        level: 'medium',
        scope: 'local',
        message: error,
      },
    })
  }

  switch (appError) {
    case 'validation_error':
      const err = error as ApiError
      if (err && err.data && err?.data?.errors) options?.onValidationError?.(err.data.errors)
      return appError

    case 'cart_expired':
      setError('Your cart has expired.')
      toast.error('Your cart has expired.')
      options?.onCartExpired?.()
      return appError

    case 'cart_not_found':
      setError('Your cart has expired.')
      toast.error('Cart not found.')
      options?.onCartNotFound?.()
      return appError

    case 'insufficient_availability':
      toast.error('Some tickets are no longer available.')
      return appError

    case 'payment_failed':
      toast.error('Payment failed. Please try again.')
      return appError

    case 'unauthorized':
      toast.error('You are not authorised.')
      options?.onUnauthorized?.()
      return appError

    case 'internal_error':
      toast.error('Network error. Please check your connection.')
      options?.onInternalError?.()
      return appError

    case 'bad_request':
      setError('Bad request, please start again or contact support')
      return appError

    case 'network_error':
      toast.error('Network error. Please check your connection.')
      return appError

    case 'unknown':
    default:
      toast.error('Something went wrong.')
      return appError
  }
}
